import React from 'react';
import { Container, Typography, Box, Paper } from '@mui/material';
import { PageHeader } from '../components/common/PageHeader';
import SEO from '../components/SEO';

const RideResponsiblyPage: React.FC = () => {
  return (
    <>
      <SEO 
        title="Ride Responsibly - CyclingBuddy"
        description="Important information about using CyclingBuddy's route planning features, data sources, and user responsibilities."
      />
      <div className="privacy-policy-page">
        <Container maxWidth="md" className="privacy-policy-container">
          <Paper elevation={2} className="privacy-policy-paper">
            {/* Header with back button and title */}
            <PageHeader title="Ride Responsibly" titleVariant="h5" />

            <Typography 
              variant="h5" 
              component="h3" 
              className="privacy-policy-section-title"
            >
              About Our Route Planning
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              CyclingBuddy helps you plan your cycling adventures by suggesting routes based on publicly available map data. 
              We use <strong>OpenStreetMap</strong> (OSM) and the <strong>BRouter</strong> routing engine to create these suggestions.
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              Here's the thing: OSM is an amazing community-driven project, but it's maintained by volunteers worldwide. 
              This means while the data is generally great, it might not always be 100% current or complete. Roads can change, 
              new paths appear, and sometimes the data might be outdated.
            </Typography>

            <Typography 
              variant="h5" 
              component="h3" 
              className="privacy-policy-section-title"
            >
              Your Responsibility as a Cyclist
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              We love helping you discover new routes, but we're not riding alongside you (we wish we could! 🚴). 
              That means you're in charge of making sure your chosen route is safe and suitable for your ride.
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              Please take time to:
            </Typography>
            <Box component="ul" className="privacy-policy-text" sx={{ ml: 2 }}>
              <li>Check the actual road conditions before you ride</li>
              <li>Verify that cycling is allowed on your planned route</li>
              <li>Follow all local traffic laws and regulations</li>
              <li>Consider your skill level and the route difficulty</li>
              <li>Check weather conditions and plan accordingly</li>
              <li>Let someone know your planned route for safety</li>
            </Box>

            <Typography 
              variant="h5" 
              component="h3" 
              className="privacy-policy-section-title"
            >
              No Guarantees
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              We provide CyclingBuddy as a helpful tool, but we can't guarantee that routes will be perfect, safe, or exactly as mapped. 
              Real-world conditions – like road closures, construction, weather, or traffic – can differ from what's shown on the map.
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              By using our route planner, you understand and accept that you're responsible for your own safety and decisions while cycling. 
              CyclingBuddy and its creators cannot be held liable for any accidents, injuries, or issues that might occur during your rides.
            </Typography>

            <Typography 
              variant="h5" 
              component="h3" 
              className="privacy-policy-section-title"
            >
              Data Sources & Credits
            </Typography>
            <Typography paragraph className="privacy-policy-text">
              Our routing features are powered by:
            </Typography>
            <Box component="ul" className="privacy-policy-text" sx={{ ml: 2 }}>
              <li>
                <strong>OpenStreetMap:</strong> Map data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">
                  OpenStreetMap contributors
                </a>
              </li>
              <li>
                <strong>BRouter:</strong> Routing engine for bicycle navigation
              </li>
            </Box>
            <Typography paragraph className="privacy-policy-text">
              We're grateful to these open-source communities for making tools like CyclingBuddy possible! 🙏
            </Typography>
          </Paper>
        </Container>
      </div>
    </>
  );
};

export default RideResponsiblyPage;
